import { Doughnut } from "react-chartjs-2";
import { formatCurrency } from "@/utils/formatters";
import {
  getDoughnutChartOptions,
  generateDoughnutChartData,
} from "@/utils/chartConfig";
import { ChartPieIcon } from "@heroicons/react/24/outline";

interface ServiceAnalyticsProps {
  data: Array<{
    service: string;
    cost: number;
    calls: number;
    percentage: number;
  }>;
}

const COLORS = [
  "#06ec9e", // Primary green
  "#009454", // Dark green
  "#f59e0b", // Accent yellow
  "#3b82f6", // Highlight blue
  "#10b981", // Success green
  "#ef4444", // Danger red
  "#8b5cf6", // Purple
  "#06b6d4", // Cyan
];

export const ServiceAnalytics: React.FC<ServiceAnalyticsProps> = ({ data }) => {
  const chartData = generateDoughnutChartData(
    data.map((d) => d.service),
    data.map((d) => d.cost),
    data.map((_d, index) => COLORS[index % COLORS.length])
  );

  const totalCost = data.reduce((sum, d) => sum + (d.cost || 0), 0);

  const options = getDoughnutChartOptions({
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        titleColor: "rgb(15, 23, 42)",
        bodyColor: "rgb(71, 85, 105)",
        borderColor: "rgba(6, 236, 158, 0.2)",
        borderWidth: 1,
        padding: 12,
        cornerRadius: 8,
        titleFont: {
          size: 14,
          weight: "bold",
        },
        bodyFont: {
          size: 13,
        },
        displayColors: true,
        callbacks: {
          label: (context: { label?: string; parsed: number; dataset: { data: number[] } }) => {
            const label = context.label || "";
            const value = formatCurrency(context.parsed);
            const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
            const percentage = ((context.parsed / total) * 100).toFixed(1);

            // Find the service data to get calls
            const serviceData = data.find((d) => d.service === label);
            const calls = serviceData?.calls || 0;

            return [
              `${label}: ${value}`,
              `Percentage: ${percentage}%`,
              `Calls: ${calls.toLocaleString()}`,
            ];
          },
        },
      },
    },
  });

  return (
    <div className="group p-8 rounded-xl border shadow-xl backdrop-blur-xl transition-all duration-300 glass border-primary-200/30 dark:border-primary-500/20 bg-gradient-light-panel dark:bg-gradient-dark-panel hover:scale-[1.01] hover:shadow-2xl">
      <div className="flex items-center gap-3 mb-6">
        <div className="p-3 rounded-xl shadow-lg bg-gradient-primary glow-primary group-hover:scale-110 transition-transform duration-300">
          <ChartPieIcon className="w-6 h-6 text-white" />
        </div>
        <h3 className="text-xl font-bold font-display gradient-text-primary">
          Service Breakdown
        </h3>
      </div>
      <div className="relative h-64 chart-container rounded-xl">
        <Doughnut data={chartData} options={options} />
        <div className="flex absolute inset-0 justify-center items-center pointer-events-none">
          <div className="text-center">
            <p className="text-3xl font-display font-bold gradient-text-primary">
              {formatCurrency(totalCost)}
            </p>
            <p className="text-sm font-body text-light-text-secondary dark:text-dark-text-secondary">Total</p>
          </div>
        </div>
      </div>
      <div className="mt-6 space-y-3">
        {data.map((service) => (
          <div
            key={service.service}
            className="group/item flex justify-between items-center p-4 rounded-xl border shadow-lg backdrop-blur-xl glass border-primary-200/30 dark:border-primary-500/20 bg-white/50 dark:bg-dark-bg-300/50 hover:bg-primary-500/5 dark:hover:bg-primary-500/10 transition-all duration-300 hover:scale-[1.01] hover:shadow-xl"
          >
            <div className="flex gap-3 items-center">
              <div
                className="w-4 h-4 rounded-full shadow-lg border border-white/50 dark:border-gray-700/50"
                style={{
                  backgroundColor: COLORS[data.indexOf(service) % COLORS.length],
                }}
              />
              <span className="text-sm font-display font-semibold text-light-text-primary dark:text-dark-text-primary">
                {service.service}
              </span>
            </div>
            <div className="text-right">
              <p className="text-lg font-display font-bold gradient-text-primary">
                {formatCurrency(service.cost || 0)}
              </p>
              <p className="text-xs font-body text-light-text-secondary dark:text-dark-text-secondary">
                {service.calls.toLocaleString()} calls
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
